import React, { useEffect, useRef } from "react";
import { Button } from "@penmate/react-shared/ui/button";
import { scheduleCelebration } from "./confetti";

// The sequence from here: write, add photos or a postcard, we deliver.
const NEXT: Array<{ title: string; detail: string }> = [
  {
    title: "Write your message",
    detail: "Type it right here on your phone.",
  },
  {
    title: "Send photos or postcards",
    detail: "Add photos to your message, or send a postcard on its own.",
  },
  {
    title: "We'll handle delivery",
    detail: "You'll get a notification once it's delivered.",
  },
];

// The handoff: the peak-motivation moment. One job — the shortest path from
// "added" to a started message.
export const HandoffStep = ({
  firstName,
  onCompose,
  onDone,
}: {
  firstName: string;
  onCompose: () => void;
  onDone: () => void;
}) => {
  // Leaving by either button keeps this page alive in the host's navigation
  // stack, so the celebration is stopped on exit, not only on unmount.
  const stopCelebration = useRef<(() => void) | undefined>(undefined);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    stopCelebration.current = scheduleCelebration();
    if (headingRef.current) headingRef.current.focus();
    return () => {
      if (stopCelebration.current) stopCelebration.current();
    };
  }, []);
  const leave = (go: () => void) => {
    if (stopCelebration.current) stopCelebration.current();
    go();
  };

  return (
    <div className="flex w-full flex-col gap-5" role="region" aria-label="Added">
      <div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-[28px] font-extrabold leading-[1.15] tracking-[-0.02em] text-foreground outline-none"
        >
          You can now connect with {firstName}
        </h1>
        <p className="mt-2 text-base leading-relaxed text-muted-foreground">
          Here&rsquo;s what happens next:
        </p>
      </div>

      <ol role="list" className="flex flex-col gap-4">
        {NEXT.map((step, i) => (
          <li key={step.title} className="flex items-start gap-4">
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--teal-bright)/0.15)] text-[15px] font-bold text-primary"
            >
              {i + 1}
            </span>
            <span>
              <span className="block text-base font-semibold text-foreground">{step.title}</span>
              <span className="mt-0.5 block text-sm leading-snug text-muted-foreground">
                {step.detail}
              </span>
            </span>
          </li>
        ))}
      </ol>

      <div className="flex flex-col gap-2">
        <Button
          size="lg"
          className="h-[52px] w-full rounded-xl text-[17px] font-semibold"
          onClick={() => leave(onCompose)}
        >
          Send {firstName} a message
        </Button>
        <button
          type="button"
          className="min-h-[44px] text-[15px] font-semibold text-primary"
          onClick={() => leave(onDone)}
        >
          Done for now
        </button>
      </div>
    </div>
  );
};
